import React, { useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import { Header } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import FundSelector from '../../components/Finance/FundSelector';

const GROUP_BY_LABEL = { category: 'Category', month: 'Month', fund: 'Fund' };

const FinanceReports = () => {
  useKYCStatusRedirect('/finance/reports');
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [fundId, setFundId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('category');
  const [reportType, setReportType] = useState('both');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const buildParams = (format) => {
    const params = new URLSearchParams({ groupBy, reportType });
    if (fundId) params.set('fund_id', fundId);
    if (startDate) params.set('startDate', new Date(startDate).toISOString().slice(0, 10));
    if (endDate) params.set('endDate', new Date(endDate).toISOString().slice(0, 10));
    if (format) params.set('format', format);
    return params;
  };

  const fetchReport = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = buildParams('json');
      const { data } = await userRequest.get(`/schools/${schoolId}/finance/reports?${params.toString()}`);
      setReport(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId, groupBy, reportType]);

  const downloadCSV = async () => {
    if (!schoolId) return;
    setDownloading(true);
    try {
      const params = buildParams('csv');
      const response = await userRequest.get(`/schools/${schoolId}/finance/reports?${params.toString()}`, {
        responseType: 'blob',
      });
      const disposition = response.headers?.['content-disposition'];
      const match = disposition && /filename="?([^";]+)"?/.exec(disposition);
      const filename = match?.[1] || `finance-report-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setDownloading(false);
    }
  };

  const isCombinedReport = report?.reportType === 'both';
  const rows = isCombinedReport
    ? [
      ...(report?.expenses || []).map(row => ({ ...row, entryType: 'Expense' })),
      ...(report?.income || []).map(row => ({ ...row, entryType: 'Income' })),
    ]
    : (report?.rows || []).map(row => ({
      ...row,
      entryType: report?.reportType === 'income' ? 'Income' : 'Expense',
    }));

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex items-start justify-between mb-6">
        <Header
          category="Finance"
          title="Financial Reports"
          description="Generate comprehensive financial statements, income vs expense breakdowns, and downloadable audit reports."
        />
        <button
          type="button"
          onClick={downloadCSV}
          disabled={downloading}
          className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors disabled:opacity-50"
        >
          <FiDownload className="w-4 h-4" />
          {downloading ? 'Preparing...' : 'Download Report'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="w-48">
          <FundSelector schoolId={schoolId} value={fundId} onChange={setFundId} includeAllOption />
        </div>
        <select
          value={groupBy}
          onChange={e => setGroupBy(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="category">Group by Category</option>
          <option value="month">Group by Month</option>
          <option value="fund">Group by Fund</option>
        </select>
        <select
          value={reportType}
          onChange={e => setReportType(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="both">Income & Expenses</option>
          <option value="expense">Expenses Only</option>
          <option value="income">Income Only</option>
        </select>
        <input
          type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-3 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-3 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={fetchReport}
          className="py-2.5 px-5 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-full transition-colors"
        >
          Apply
        </button>
      </div>

      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isCombinedReport && <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Type</th>}
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">{GROUP_BY_LABEL[groupBy]}</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Total</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={isCombinedReport ? 4 : 3} className="px-5 py-10 text-center text-sm text-gray-400">Loading report...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isCombinedReport ? 4 : 3} className="px-5 py-10 text-center text-sm text-gray-400">No data for this selection.</td></tr>
            ) : rows.map((row, idx) => (
              <tr key={`${row.entryType}-${row._id || idx}`}>
                {isCombinedReport && (
                  <td className="px-5 py-3 text-sm">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.entryType === 'Income' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {row.entryType}
                    </span>
                  </td>
                )}
                <td className="px-5 py-3 text-sm text-gray-700 capitalize">{row.label || row.category_name || row._id}</td>
                <td className="px-5 py-3 text-sm font-semibold text-gray-800">{formatCurrency(row.total)}</td>
                <td className="px-5 py-3 text-sm text-gray-500">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinanceReports;
